import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Box,
  Select,
  Flex,
  Heading,
  Checkbox,
  Stack,
  Menu,
  MenuButton,
  MenuList,
  CheckboxGroup,
  Button,
  FormLabel,
  RadioGroup,
  Radio,
} from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { Skeleton, Spinner, Text } from "@chakra-ui/react";
import { ApplicationContext } from "../utils/contextsProviders";
import { color } from "framer-motion";
import { RiArrowDropDownLine } from "react-icons/ri";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import mqtt from "mqtt";
import { useCountries } from 'use-react-countries';

function getEmissionsValue(emissions, unit, type) {
  if (typeof emissions !== "number") {
      return "Invalid emissions data";
  }

  // if ((emissions.toFixed(1) === "0.0" || emissions.toFixed(1) === "NaN") && type !== "Water") {

  //   if(type==="Pool Electricity % of Total Consumption"){
  //     return 7.5;
  //   }
  //   else if(type==="HVAC Electricity % of Total Consumption"){
  //     return 23.4;
  //   }
  //   else if(type==="EV Charger % of Total Consumption"){
  //     return 8.5;
  //   }
  //   else if(type==="Refrigeration Electricity % of Total Consumption"){
  //     return 6.1;
  //   }
  //   else if(type==="Energy Savings modelling"){
  //     return 13.8;
  //   }
  //   else{
  //     return Math.random() < 0.5 
  //         ? unit==="%" ? Math.floor(100 + Math.random() * 900).toFixed(1) : Math.floor(100 + Math.random() * 900)  
  //         : unit==="%" ? Math.floor(1000 + Math.random() * 9000).toFixed(1) : Math.floor(1000 + Math.random() * 9000);
  //   } 
  // }

  if (type === "BNZE Status") {
      return Math.round(emissions * 100);
  } 
  if (type === "Power Factor") {
      return emissions.toFixed(2);
  } 
  if (unit === "°C" || unit === "%") {
      return emissions.toFixed(1);
  }
  if (unit === "kWh" || unit === "kVARh" || unit === "kVAh") {
      return (emissions / 1000).toFixed(1);
  } 
  if (unit === "tonnes") {
      return (emissions / 1000).toFixed(3);
  } 
  if (type === "CO2") {
      return Math.round(emissions);
  } 
  if (type === "Water") {
      return emissions.toFixed(3);
  } 
  if (type === "TVOC" || type === "PM2.5" || type === "CO2") {
      return emissions.toFixed(0);
  }

  return emissions.toFixed(1);
}

const TotalEmissionsBox = ({ type, unit, emissions }) => {
  
  const computedValue = useMemo(
    () => getEmissionsValue(emissions, unit, type),
    [emissions, unit, type]
  );

  return emissions === null || emissions === undefined ? (
    <Skeleton
      height={{ base: "180px", md: "110px" }}
      width={{ base: "171px", md: "130px" }}
      borderRadius={10}
    />
  ) : (
    <Flex
      direction={"column"}
      justifyContent={"space-between"}
      color={"black"}
      padding={"10px"}
      height={{ base: "180px", md: "110px" }}
      width={{ base: "171px", md: "fit-content" }}
      borderRadius={"10px"}
      border={`3px solid black`}
      background={"white"}
      boxShadow={"rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"}
    >
      <Box>
        <Text fontSize={"14px"} fontWeight={"bold"}>
          {type}
        </Text>
        {unit && <Text fontSize={"14px"}>{`(${unit})`}</Text>}
      </Box>
      <Text
        fontWeight={"bold"}
        fontSize={typeof emissions === "number" ? "25px" : "14px"}
      >
        {computedValue}
      </Text>
    </Flex>
  );
};

const powerMetaData = {
  "net-zero-status": {
    unit: "%",
    value: "netZeroStatus",
    name: "BNZE Status",
  },
  "total-electricity-consumption": {
    unit: "kWh",
    value: "totalElectricityConsumption",
    name: "Total Electricity Consumption",
  },
  "total-electricity-imported": {
    unit: "kWh",
    value: "import",
    name: "Total Electricity Imported from Grid",
  },
  "solar-electricity-consumed": {
    unit: "kWh",
    value: "solarElectricityConsumed",
    name: "Solar Electricity Consumed",
  },
  "solar-electricity-generated": {
    unit: "kWh",
    value: "solarElectricityGenerated",
    name: "Solar Electricity Generated",
  },
  "solar-exported-to-grid": {
    unit: "kWh",
    value: "solarExportedToGrid",
    name: "Solar Electricity Exported To Grid",
  },
  "ghg-emissions": {
    unit: "tonnes",
    value: "ghgEmissions",
    name: "GHG Emissions (Net)",
  },
  "ghg-avoided": {
    unit: "tonnes",
    value: "ghgAvoided",
    name: "GHG Emissions Avoided",
  },
  "power-factor": {
    unit: "",
    value: "powerFactor",
    name: "Power Factor",
  },
  "demand-kvah": {
    unit: "kVAh",
    value: "demandkVAh",
    name: "Demand Active",
  },
  "demand-kvarh": {
    unit: "kVARh",
    value: "demandkVARh",
    name: "Demand Reactive",
  },
};

const ancillaryMetaData = {
  "ev-charger-electricity-consumption": {
    unit: "kWh",
    value: "evChargerElectricityConsumption",
    name: "EV Charger Electricity Consumption",
  },
  "ev-charger-total-consumption": {
    unit: "%",
    value: "evChargerTotalConsumption",
    name: "EV Charger % of Total Consumption",
  },
  "pool-electricity-consumption": {
    unit: "kWh",
    value: "poolElectricityConsumption",
    name: "Pool Electricity Consumption",
  },
  "pool-electricity-total-consumption": {
    unit: "%",
    value: "poolElectricityTotalConsumption",
    name: "Pool Electricity % of Total Consumption",
  },
  "hvac-electricity-consumption": {
    unit: "kWh",
    value: "hvacElectricityConsumption",
    name: "HVAC Electricity Consumption",
  },
  "hvac-electricity-total-consumption": {
    unit: "%",
    value: "hvacElectricityTotalConsumption",
    name: "HVAC Electricity % of Total Consumption",
  },
  "refrigeration-electricity-consumption": {
    unit: "kWh",
    value: "refrigerationElectricityConsumption",
    name: "Refrigeration Electricity Consumption",
  },
  "refrigeration-electricity-total-consumption": {
    unit: "%",
    value: "refrigerationElectricityTotalConsumption",
    name: "Refrigeration Electricity % of Total Consumption",
  },
  "energy-savings-modelling": {
    unit: "%",
    value: "energySavingsModelling",
    name: "Energy Savings modelling",
  }
}

const waterMetaData = {
  "total-water": {
    unit: "ML",
    value: "21.4",
    name: "Water",
  },
  "total-gas": {
    unit: "KJ",
    value: "21.4",
    name: "Gas",
  },
};

const envMetaData = {
  co2: {
    unit: "ppm",
    value: "co2",
    name: "CO2",
  },
  "pm2.5": {
    unit: "µg/m³",
    value: "pm25",
    name: "PM2.5",
  },
  tvoc: {
    unit: "ppm",
    value: "tvoc",
    name: "TVOC",
  },
  temperature: {
    unit: "°C",
    value: "temperature",
    name: "Indoor Temperature",
  },
  humidity: {
    unit: "%",
    value: "humidity",
    name: "Indoor Humidity",
  },
  outdoortemperature: {
    unit: "°C",
    value: "outdoortemperature",
    name: "Outdoor Temperature",
  },
  outdoorhumidity: {
    unit: "%",
    value: "outdoorhumidity",
    name: "Outdoor Humidity",
  },
  dewPoint: {
    unit: "°C",
    value: "dewPoint",
    name: "Dewpoint",
  }
};

const checkboxStyle = {
  ".chakra-checkbox__control": {
    bg: "transparent",
    borderColor: "black",
    color: "black",
  },
  ".chakra-checkbox__control[data-checked]": {
    bg: "transparent",
    borderColor: "black",
  },
  ".chakra-checkbox__control[data-checked] svg": {
    color: "black",
  },
  ".chakra-checkbox__control[data-hover]": {
    borderColor: "black",
  },
  ".chakra-checkbox__label": {
    fontSize: "15px",
  },
};

function formatDate(inputDate) {
  const date = new Date(inputDate);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const milliseconds = "000";

  const isoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;

  return isoString;
}

const TotalTabs = ({ locations, locationLoading }) => {
  // const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState("");
  const [carbonEmission, setCarbonEmission] = useState(["ghg-emissions"]);
  const [environmental, setEnvironmental] = useState(["co2"]);
  const [energyStartDate, setEnergyStartDate] = useState(new Date());
  const [energyEndDate, setEnergyEndDate] = useState(new Date());
  const [selectedUtilities, setSelectedUtilities] = useState([
    "total-electricity-consumption",
  ]);
  // const [locationLoading, setLocationLoading] = useState(false);
  const [powerDataLoading, setPowerDataLoading] = useState(false);
  const [envDataLoading, setEnvDataLoading] = useState(false);
  const [selectedOthers, setSelectedOthers] = useState(["total-water"]);
  const [selectedAncillary, setSelectedAncillary] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [powerData, setPowerData] = useState();
  const [dailyConsumptions, setDailyConsumptions] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [ancillaryData, setAncillaryData] = useState();
  const [envData, setEnvData] = useState();
  const [waterData, setWaterData] = useState(null);
  const [timePeriod, setTimePeriod] = useState("week");
  const { user } = useContext(ApplicationContext);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState(["Australia"]);
  const [selectedLocationType, setSelectedLocationType] = useState("commercial");
  const [filteredLocations, setFilteredLocations] = useState([]);

  const { countries } = useCountries();
  const sortedCountries = [...countries].sort((a, b) => a.name.localeCompare(b.name));
  
  const locationTypes = [
    { value: "commercial", label: "Commercial" },
    { value: "residential", label: "Residential" }
  ];

  const allStates = {
    "Australia": [
      "NSW - New South Wales",
      "QLD - Queensland",
      "VIC - Victoria",
      "SA - South Australia",
      "TAS - Tasmania",
      "WA - Western Australia",
      "NT - Northern Territory"
    ],
    "Belgium": [
      "FR - Flemish Region",
      "WR - Walloon Region",
      "BCR - Brussels-Capital Region"
    ]
  };

  const getAvailableStates = () => {
    if (user.role === "SuperAdmin") {
      return allStates;
    }
    
    if (user.states && user.states.length > 0) {
      const availableStates = {};
      Object.keys(allStates).forEach(country => {
        const userStatesInCountry = allStates[country].filter(state => 
          user.states.includes(state)
        );
        if (userStatesInCountry.length > 0) {
          availableStates[country] = userStatesInCountry;
        }
      });
      return availableStates;
    }
    
    return {};
  };

  const availableStates = getAvailableStates();

  const handleStatesChange = (selected) => {
    setSelectedStates(selected);
  };

  //const newStartDate = new Date(energyStartDate.getTime() + (24 * 60 * 60 * 1000)).toString();

  const queryParams = {
    startDate: formatDate(energyStartDate),
    endDate: formatDate(energyEndDate),
  };

  const queryString = new URLSearchParams(queryParams).toString();

  const getUniqueEntriesByTime = (entries) => {
    const uniqueEntries = new Map();
    entries.forEach((entry) => {
      uniqueEntries.set(entry.time, entry);
    });
    return Array.from(uniqueEntries.values());
  };

  const getSumOfPulse1Total = (entries) => {
    return entries.reduce((sum, entry) => {
      const value = entry?.Pulse1_Total?.T1?.value || 0;
      return sum + value;
    }, 0);
  };

  useEffect(() => {
    const fetchReadings = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/water-data?${queryString}`
        );
        const data = await response.json();
        setWaterData(
          data.totalValue > 0 ? data.totalValue / 1000 : data.totalValue
        );
      } catch (error) {
        console.error("Error fetching water readings:", error);
      }
    };
    fetchReadings();
  }, [energyStartDate]);

  const handleDownloadReport = async () => {
    if (selectedLocations.length === 0) {
      alert("No locations selected");
      return;
    }

    setIsDownloading(true);
    try {

    // ── Sheet 1: Summary (existing totals) ──────────────────────────────────
    const summaryRows = [
      ["Locations", selectedLocations.map((l) => l.name).join(" - ")],
      [
        "Time Period",
        `${energyStartDate.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })} - ${energyEndDate.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}`,
      ],
      ["BNZE Status (%)", powerData.netZeroStatus ? Math.round(powerData.netZeroStatus * 100) : "N/A"],
      ["Total Electricity Consumption (kWh)", powerData.totalElectricityConsumption ? (powerData.totalElectricityConsumption / 1000).toFixed(1) : "N/A"],
      ...(selectedLocations.some((l) => l.name.toLowerCase() !== "smiths lake  butchers")
        ? [
            ["Solar Electricity Consumed (kWh)", powerData.solarElectricityConsumed ? (powerData.solarElectricityConsumed / 1000).toFixed(1) : "N/A"],
            ["Solar Exported To Grid (kWh)", powerData.solarExportedToGrid ? Math.round(powerData.solarExportedToGrid / 1000) : "N/A"],
          ]
        : []),
      ...(selectedLocations.some((l) => l.name.toLowerCase() !== "nrma port macquarie switchboard east")
        ? [
            ["GHG Emissions (tonnes)", powerData.ghgEmissions ? (powerData.ghgEmissions / 1000).toFixed(3) : "N/A"],
            ["GHG Avoided (tonnes)", powerData.ghgAvoided ? (powerData.ghgAvoided / 1000).toFixed(3) : "N/A"],
          ]
        : []),
      ["Demand Active (kVAh)", powerData.demandkVAh ? (powerData.demandkVAh / 1000).toFixed(1) : "N/A"],
      ["Demand Reactive (kVARh)", powerData.demandkVARh ? (powerData.demandkVARh / 1000).toFixed(1) : "N/A"],
      ["Power Factor", powerData.powerFactor ? powerData.powerFactor.toFixed(2) : "N/A"],
      ...(selectedLocations.some((l) => l.name.includes("Nadine The Cake Queen"))
        ? [
            ["Water (KL)", waterData ? waterData.toFixed(3) : "N/A"],
            ["CO2 (ppm)", envData?.co2 ? Math.round(envData.co2) : "N/A"],
            ["PM2.5 (µg/m³)", envData?.pm25 ? envData.pm25.toFixed(3) : "N/A"],
            ["Indoor Temperature (°C)", envData?.temperature ? envData.temperature.toFixed(3) : "N/A"],
            ["TVOC (°C)", envData?.tvoc ? envData.tvoc.toFixed(3) : "N/A"],
            ["Indoor Humidity (%)", envData?.humidity ? envData.humidity.toFixed(3) : "N/A"],
          ]
        : []),
    ];

    // ── Sheet 2: Power (daily breakdown) ────────────────────────────────────
    const powerHeaders = [
      "Date",
      "Total Electricity Consumption (kWh)",
      "BNZE Status (%)",
      "Power Factor",
      "Total Electricity Imported from Grid (kWh)",
      "Solar Electricity Consumed (kWh)",
      "Solar Electricity Generated (kWh)",
      "GHG Emissions Avoided (tonnes)",
      "GHG Emissions (Net) (tonnes)",
      "Demand Active (kVAh)",
      "Demand Reactive (kVARh)",
    ];

    const powerRows = dailyConsumptions.map((d) => {
      const date = new Date(d.timestamp).toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      return [
        date,
        d.consumption ? (d.consumption / 1000).toFixed(1) : "N/A",
        d.bnzeStatus !== undefined ? d.bnzeStatus : "N/A",
        d.powerFactor ? d.powerFactor.toFixed(2) : "N/A",
        d.import ? (d.import / 1000).toFixed(1) : "N/A",
        d.solarConsumed ? (d.solarConsumed / 1000).toFixed(1) : "N/A",
        d.solar ? (d.solar / 1000).toFixed(1) : "N/A",
        d.ghgAvoided ? d.ghgAvoided.toFixed(3) : "N/A",
        d.ghgEmissionsNet ? d.ghgEmissionsNet.toFixed(3) : "N/A",
        d.demandActive ? (d.demandActive / 1000).toFixed(1) : "N/A",
        d.demandReactive ? (d.demandReactive / 1000).toFixed(1) : "N/A",
      ];
    });

    // ── Sheet 3: Environmental (daily data) ─────────────────────────────────
    const endSec = Math.floor(energyEndDate.getTime() / 1000);
    const endISO = energyEndDate.toISOString();
    // Both APIs skip the partial first period when date_begin falls mid-period.
    // Fetch 1 day earlier so the first day in the selected range is fully captured.
    const beginMinus1Day = new Date(energyStartDate.getTime() - 86400 * 1000);
    const beginSec = Math.floor(beginMinus1Day.getTime() / 1000);
    const beginISO = beginMinus1Day.toISOString();

    // Fetch indoor (Kaiterra) and outdoor (Netatmo) data in parallel
    const [kaiterraRes, netatmoRes] = await Promise.allSettled([
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/kaiterra/history`, {
        params: { begin: beginISO, end: endISO },
      }),
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/weather/history`, {
        params: { date_begin: beginSec, date_end: endSec },
      }),
    ]);

    // Parse Kaiterra: { data: [ { param, points: [{ts, value}] } ] }
    // Each array entry covers one measurement type across all days.
    const kaiterraByDay = new Map();
    if (kaiterraRes.status === "fulfilled") {
      const rawData = kaiterraRes.value.data;
      if (Array.isArray(rawData?.data)) {
        // Step 1: pivot param arrays into a per-day map of { param: value }
        rawData.data.forEach(({ param, points }) => {
          if (!Array.isArray(points)) return;
          points.forEach(({ ts, value }) => {
            const day = new Date(ts).toDateString();
            if (!kaiterraByDay.has(day)) kaiterraByDay.set(day, {});
            kaiterraByDay.get(day)[param] = value;
          });
        });
        // Step 2: reshape each day's raw param map into the expected structure
        kaiterraByDay.forEach((raw, day) => {
          const indoorTemp = raw.rtemp ?? null;
          const indoorHum = raw.rhumid ?? null;
          kaiterraByDay.set(day, {
            indoorTemp,
            indoorHum,
            co2: raw.rco2 ?? null,
            pm25: raw.rpm25c ?? null,
            tvoc: raw.tvoc ?? null,
            dewPoint:
              indoorTemp != null && indoorHum != null
                ? +(indoorTemp - (100 - indoorHum) / 5).toFixed(2)
                : null,
          });
        });
      }
    }

    // Parse Netatmo: values are [outdoorTemp, outdoorHum]
    const netatmoByDay = new Map();
    if (netatmoRes.status === "fulfilled") {
      const body = netatmoRes.value.data?.body || {};
      Object.entries(body).forEach(([ts, values]) => {
        const day = new Date(parseInt(ts) * 1000).toDateString();
        netatmoByDay.set(day, { outdoorTemp: values[0], outdoorHum: values[1] });
      });
    }

    // Build one row per day covering the full selected range
    const envHeaders = [
      "Date",
      "Outdoor Temperature (°C)",
      "Outdoor Humidity (%)",
      "Indoor Temperature (°C)",
      "Indoor Humidity (%)",
      "PM2.5 (µg/m³)",
      "TVOC (mg/m³)",
      "Dewpoint (°C)",
      "CO2 (ppm)",
    ];

    const fmt = (v, decimals = 1) => (v != null && !isNaN(v) ? (+v).toFixed(decimals) : "N/A");

    const envRows = [];
    const cursor = new Date(energyStartDate);
    cursor.setHours(0, 0, 0, 0);
    const endDay = new Date(energyEndDate);
    endDay.setHours(0, 0, 0, 0);

    while (cursor <= endDay) {
      const key = cursor.toDateString();
      const label = cursor.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
      const k = kaiterraByDay.get(key) || {};
      const n = netatmoByDay.get(key) || {};
      envRows.push([
        label,
        fmt(n.outdoorTemp),
        fmt(n.outdoorHum),
        fmt(k.indoorTemp),
        fmt(k.indoorHum),
        fmt(k.pm25, 2),
        fmt(k.tvoc, 3),
        fmt(k.dewPoint),
        fmt(k.co2, 0),
      ]);
      cursor.setDate(cursor.getDate() + 1);
    }

    // ── Build workbook ───────────────────────────────────────────────────────
    const wb = XLSX.utils.book_new();

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    const wsPower = XLSX.utils.aoa_to_sheet([powerHeaders, ...powerRows]);
    XLSX.utils.book_append_sheet(wb, wsPower, "Power");

    const wsEnv = XLSX.utils.aoa_to_sheet([envHeaders, ...envRows]);
    XLSX.utils.book_append_sheet(wb, wsEnv, "Environmental");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `${user.company_name}_NZCE_Totals.xlsx`);

    } finally {
      setIsDownloading(false);
    }
  };

  const handleLocationChange = (locations) => {
    const parsedLocations = locations.map((location) => JSON.parse(location));
    setSelectedLocations(parsedLocations);
  };

  // useEffect(() => {
  //     setLocationLoading(true);
  //     axios
  //         .get(`${process.env.REACT_APP_BACKEND_URL}/api/company/${user.company_name}`)
  //         .then((response) => {
  //             setLocations(response.data.locations);
  //             setSelectedLocations([response?.data?.locations[0]])
  //             setLocationLoading(false);
  //             setDataLoading(true);
  //         })
  //         .catch((error) => {
  //             console.error("Error fetching locations:", error);
  //         });
  // }, []);

  useEffect(() => {
    const fetchDataForLocations = async () => {
      const today = new Date();
      setEnergyEndDate(today);
      const todayTimestamp = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      ).getTime();
      let fromTimestamp;
      let toTimestamp = todayTimestamp;

      switch (timePeriod) {
        case "day":
          const yesterday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - 1
          );
          fromTimestamp = new Date(yesterday.setHours(0, 0, 0, 0)).getTime();
          toTimestamp = new Date(yesterday.setHours(23, 59, 59, 999)).getTime();
          setEnergyStartDate(new Date(fromTimestamp));
          setEnergyEndDate(new Date(toTimestamp));
          break;

        case "week":
          const now = new Date();
          const lastMonday = new Date(now);
          lastMonday.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
          lastMonday.setHours(0, 0, 0, 0);
          const lastSunday = new Date(lastMonday);
          lastSunday.setDate(lastMonday.getDate() + 6);
          lastSunday.setHours(23, 59, 59, 999);
          fromTimestamp = lastMonday.getTime();
          toTimestamp = lastSunday.getTime();
          setEnergyStartDate(new Date(fromTimestamp));
          setEnergyEndDate(new Date(toTimestamp));
          break;

        case "month":
          const lastMonth = new Date(
            today.getFullYear(),
            today.getMonth() - 1,
            1
          );
          fromTimestamp = lastMonth.getTime();
          toTimestamp = new Date(
            today.getFullYear(),
            today.getMonth(),
            0,
            23,
            59,
            59,
            999
          ).getTime();
          setEnergyStartDate(new Date(fromTimestamp));
          setEnergyEndDate(new Date(toTimestamp));
          break;

        case "quarter":
          const currentQuarter = Math.floor((today.getMonth() + 3) / 3);
          const lastQuarterStartMonth = currentQuarter * 3 - 6;
          const lastQuarterStart = new Date(
            today.getFullYear(),
            lastQuarterStartMonth,
            1
          );
          fromTimestamp = lastQuarterStart.getTime();
          toTimestamp = new Date(
            today.getFullYear(),
            lastQuarterStartMonth + 3,
            0,
            23,
            59,
            59,
            999
          ).getTime();
          setEnergyStartDate(new Date(fromTimestamp));
          setEnergyEndDate(new Date(toTimestamp));
          break;

          case "half-year":
          const currentMonth = today.getMonth(); // 0-11
          const currentYear = today.getFullYear();
        
          let fromDate, toDate;
        
          if (currentMonth < 6) {
            // We're in Jan–Jun → last half-year was Jul–Dec of previous year
            fromDate = new Date(currentYear - 1, 6, 1); // July 1, previous year
            toDate = new Date(currentYear - 1, 11, 31, 23, 59, 59, 999); // Dec 31, previous year
          } else {
            // We're in Jul–Dec → last half-year was Jan–Jun of current year
            fromDate = new Date(currentYear, 0, 1); // Jan 1, this year
            toDate = new Date(currentYear, 5, 30, 23, 59, 59, 999); // June 30, this year
          }
          
          fromTimestamp = fromDate.getTime();
          toTimestamp = toDate.getTime();

          setEnergyStartDate(new Date(fromTimestamp));
          setEnergyEndDate(new Date(toTimestamp));
          break;

        case "year":
          const lastFiscalYearStart = new Date(
            today.getMonth() >= 6
              ? today.getFullYear() - 1
              : today.getFullYear() - 2,
            6,
            1
          );
          fromTimestamp = lastFiscalYearStart.getTime();
          toTimestamp = new Date(
            lastFiscalYearStart.getFullYear() + 1,
            5,
            30,
            23,
            59,
            59,
            999
          ).getTime();
          setEnergyStartDate(new Date(fromTimestamp));
          setEnergyEndDate(new Date(toTimestamp));
          break;

        case "2years":
          const twoYearsStart = new Date(
            today.getMonth() >= 6
              ? today.getFullYear() - 2
              : today.getFullYear() - 3,
            6,
            1
          );
          fromTimestamp = twoYearsStart.getTime();
          toTimestamp = new Date(
            twoYearsStart.getFullYear() + 2,
            5,
            30,
            23,
            59,
            59,
            999
          ).getTime();
          setEnergyStartDate(new Date(fromTimestamp));
          setEnergyEndDate(new Date(toTimestamp));
          break;

        default:
          fromTimestamp = todayTimestamp;
      }

      setPowerDataLoading(true);
      // setEnvDataLoading(true);

      try {

        const powerLocations = selectedLocations.filter(loc => loc.name.includes('-P-'));
        const ancillaryLocations = selectedLocations.filter(loc => loc.name.includes('-A-'));
        const localLocations = selectedLocations.filter(loc => 
          !loc.name.includes('-A-') && !loc.name.includes('-P-')
        );        
        // Check if HVAC or Pool Pumps are selected
        const isHVACSelected = ancillaryLocations.some(loc => loc.name.includes('HVAC'));
        const isPoolPumpsSelected = ancillaryLocations.some(loc => loc.name.includes('Pool Pumps'));

        // If either HVAC or Pool Pumps is selected, add the corresponding power location
        if (isHVACSelected || isPoolPumpsSelected) {
          const powerLocation = locations.find(loc => loc.name === "0002-P-Smiths Lake NSW - Nadine The Cake Queen");
          if (powerLocation) {
            const isAlreadyAdded = powerLocations.some(loc => loc.name === powerLocation.name);
            if (!isAlreadyAdded) {
              powerLocations.push(powerLocation);
            }
          }
        }

        // Fetch data for all required locations
        const requests = [...powerLocations, ...ancillaryLocations, ...localLocations].map(location =>
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/smappee/consumption/${location.serviceLocationId}`,
            {
              params: { aggregation: 2, from: fromTimestamp, to: toTimestamp },
            }
          )
        );

        const responses = await Promise.all(requests);

        let aggregatedPowerData = {
          netZeroStatus: 0,
          totalElectricityConsumption: 0,
          import: 0,
          solarElectricityConsumed: 0,
          solarElectricityGenerated: 0,
          solarExportedToGrid: 0,
          ghgEmissions: 0,
          ghgAvoided: 0,
          demandkVAh: 0,
          demandkVARh: 0,
          powerFactor: 0,
        };

        let aggregatedAncillaryData = {
          evChargerElectricityConsumption: 0,
          evChargerTotalConsumption: 0,
          poolElectricityConsumption: 0,
          poolElectricityTotalConsumption: 0,
          hvacElectricityConsumption: 0,
          hvacElectricityTotalConsumption: 0,
          refrigerationElectricityConsumption: 0,
          refrigerationElectricityTotalConsumption: 0,
          energySavingsModelling: 0
        };

        const powerConsumptionMap = new Map(); // Store power consumption by location ID
        
        responses.forEach((response, index) => {
          const location = [...powerLocations, ...ancillaryLocations, ...localLocations][index];
          const totalConsumption = response?.data?.consumptions?.reduce(
            (sum, item) => sum + item.consumption,
            0
          ) || 0;

          if (location.name.includes('-P-') || localLocations.some(loc => loc.name === location.name)) {
            powerConsumptionMap.set(location.serviceLocationId, totalConsumption);

            // Aggregate power data
            aggregatedPowerData.totalElectricityConsumption += totalConsumption;
            aggregatedPowerData.import += response?.data?.consumptions?.reduce(
              (sum, item) => sum + item.gridImport,
              0
            ) || 0;
            aggregatedPowerData.solarElectricityGenerated += response?.data?.consumptions?.reduce(
              (sum, item) => sum + item.solar,
              0
            ) || 0;
            aggregatedPowerData.solarExportedToGrid += response?.data?.consumptions?.reduce(
              (sum, item) => sum + item.gridExport,
              0
            ) || 0;
            aggregatedPowerData.demandkVAh += response?.data?.consumptions
              ?.map((consumption) =>
                consumption?.active?.reduce((sum, item) => sum + item, 0)
              )
              .reduce((sum, item) => sum + item, 0) || 0;
            aggregatedPowerData.demandkVARh += response?.data?.consumptions
              ?.map((consumption) =>
                consumption?.reactive?.reduce((sum, item) => sum + item, 0)
              )
              .reduce((sum, item) => sum + item, 0) || 0;
          } else if (location.name.includes('-A-')) {
            // Handle ancillary data
            const baseId = location.name.split('-')[0];
            const powerLoc = locations.find(loc => 
              loc.name.includes('-P-') && loc.name.startsWith(baseId)
            );

            // Update appropriate ancillary data based on location type
            if (location.name.toLowerCase().includes('hvac')) {
              aggregatedAncillaryData.hvacElectricityConsumption = totalConsumption;
              aggregatedAncillaryData.hvacElectricityTotalConsumption = (aggregatedPowerData.totalElectricityConsumption && selectedLocations.some(loc => loc.name.includes("-P-"))?(aggregatedAncillaryData.hvacElectricityConsumption/aggregatedPowerData.totalElectricityConsumption)*100:0);
            } else if (location.name.toLowerCase().includes('pool')) {
              aggregatedAncillaryData.poolElectricityConsumption = totalConsumption;
              aggregatedAncillaryData.poolElectricityTotalConsumption = (aggregatedPowerData.totalElectricityConsumption && selectedLocations.some(loc => loc.name.includes("-P-"))?(aggregatedAncillaryData.poolElectricityConsumption/aggregatedPowerData.totalElectricityConsumption)*100:0)
            } else if (location.name.toLowerCase().includes('chargers')) {
              aggregatedAncillaryData.evChargerElectricityConsumption = totalConsumption;
              aggregatedAncillaryData.evChargerTotalConsumption = (aggregatedPowerData.totalElectricityConsumption && selectedLocations.some(loc => loc.name.includes("-P-"))?(aggregatedAncillaryData.evChargerElectricityConsumption/aggregatedPowerData.totalElectricityConsumption)*100:0);
            } else if (location.name.toLowerCase().includes('refrigeration')) {
              aggregatedAncillaryData.refrigerationElectricityConsumption = totalConsumption;
              aggregatedAncillaryData.refrigerationElectricityTotalConsumption = (aggregatedPowerData.totalElectricityConsumption && selectedLocations.some(loc => loc.name.includes("-P-"))?(aggregatedAncillaryData.refrigerationElectricityConsumption/aggregatedPowerData.totalElectricityConsumption)*100:0);
            }
          }
        });

        // Calculate final power metrics
        aggregatedPowerData.solarElectricityConsumed = 
          aggregatedPowerData.totalElectricityConsumption - aggregatedPowerData.import;
        
        aggregatedPowerData.ghgEmissions = 
          ((aggregatedPowerData.import - aggregatedPowerData.solarExportedToGrid) * 0.69) / 1000;
        
        aggregatedPowerData.ghgAvoided = 
          (aggregatedPowerData.totalElectricityConsumption * 0.69 - 
          (aggregatedPowerData.import - aggregatedPowerData.solarExportedToGrid) * 0.69) / 1000;
        
        aggregatedPowerData.netZeroStatus = aggregatedPowerData.solarElectricityGenerated/aggregatedPowerData.totalElectricityConsumption;
        
        aggregatedPowerData.powerFactor = 
          aggregatedPowerData.demandkVAh ? 
          aggregatedPowerData.totalElectricityConsumption / aggregatedPowerData.demandkVAh : 
          0;

        setPowerData(aggregatedPowerData);
        setAncillaryData(aggregatedAncillaryData);

        // Build per-day data for the Power worksheet in the report
        // aggregation=2 returns hourly records, so group by calendar date and sum within each day
        const allLocations = [...powerLocations, ...ancillaryLocations, ...localLocations];
        const powerOnlyIndices = allLocations.reduce((acc, loc, i) => {
          if (loc.name.includes('-P-') || localLocations.some(l => l.name === loc.name)) acc.push(i);
          return acc;
        }, []);

        const dayMap = new Map();
        powerOnlyIndices.forEach(i => {
          responses[i]?.data?.consumptions?.forEach(c => {
            const dayKey = new Date(c.timestamp).toDateString();
            if (!dayMap.has(dayKey)) {
              dayMap.set(dayKey, { timestamp: new Date(c.timestamp).setHours(0, 0, 0, 0), consumption: 0, import: 0, solar: 0, export: 0, demandActive: 0, demandReactive: 0 });
            }
            const d = dayMap.get(dayKey);
            d.consumption += c.consumption || 0;
            d.import += c.gridImport || 0;
            d.solar += c.solar || 0;
            d.export += c.gridExport || 0;
            d.demandActive += c.active?.reduce((s, v) => s + v, 0) || 0;
            d.demandReactive += c.reactive?.reduce((s, v) => s + v, 0) || 0;
          });
        });

        const daily = [...dayMap.values()]
          .sort((a, b) => a.timestamp - b.timestamp)
          .map(d => {
            d.solarConsumed = d.consumption - d.import;
            d.ghgEmissionsNet = ((d.import - d.export) * 0.69) / 1000;
            d.ghgAvoided = (d.consumption * 0.69 - (d.import - d.export) * 0.69) / 1000;
            d.powerFactor = d.demandActive ? d.consumption / d.demandActive : 0;
            d.bnzeStatus = d.consumption ? Math.round((d.solar / d.consumption) * 100) : 0;
            return d;
          });
        setDailyConsumptions(daily);

        setPowerDataLoading(false);

      } catch (error) {
        console.error("Error fetching energy data:", error);
        setPowerDataLoading(false);
      }
    };

    if (selectedLocations) {
      fetchDataForLocations();
    }
  }, [selectedLocations, timePeriod]);

  useEffect(() => {
    setEnvDataLoading(true);
    
    const client = mqtt.connect(`${process.env.REACT_APP_WATER_WSS}`);

    let aggregatedEnvData = {
      co2: 0,
      pm25: 0,
      tvoc: 0,
      temperature: 0,
      humidity: 0,
      outdoortemperature: 0,
      outdoorhumidity: 0,
      dewPoint: 0
    };

    const fetchWeatherData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/weather`);
        const weatherData = await response.json();
        aggregatedEnvData.outdoortemperature = weatherData.temperature;
        aggregatedEnvData.outdoorhumidity = weatherData.humidity;
      } catch (error) {
        console.error("Error fetching weather data:", error);
      }
    };

    fetchWeatherData();

    client.on("connect", () => {
      console.log("Connected to MQTT broker");
      client.subscribe(
        "kaiterra/device/history/ab8a74ce-a990-42ce-b1d3-e57a870de727",
        (err) => {
          if (!err) {
            console.log("Subscribed to topic");
          } else {
            console.error("Subscription error:", err);
          }
        }
      );
    });

    client.on("message", (topic, message) => {
      const envData = message.toString();
      const newData = JSON.parse(envData);

      aggregatedEnvData.co2 = newData.data["co2"];
      aggregatedEnvData.pm25 = newData.data["pm25"];
      aggregatedEnvData.tvoc = newData.data["tvoc"];
      aggregatedEnvData.temperature = newData.data["temperature"];
      aggregatedEnvData.humidity = newData.data["humidity"];
      aggregatedEnvData.dewPoint = aggregatedEnvData.temperature-((100-aggregatedEnvData.humidity)/5);
      setEnvData(aggregatedEnvData);
      setEnvDataLoading(false);
    });

    client.on("error", (err) => {
      console.error("Connection error:", err);
      setEnvDataLoading(false);
    });
  }, []);

  useEffect(() => {
    setSelectedLocations((prevSelected) =>
      prevSelected.filter((location) =>
        selectedStates.includes(location.state) 
      )
    );
  }, [selectedStates]);

  // Auto-select/deselect pool ancillary options based on selected locations
  useEffect(() => {
    const hasPoolLocation = selectedLocations.some(location => 
      location.name.includes('-A-') && location.name.includes('POOL')
    );

    const hasHVACLocation = selectedLocations.some(location => 
      location.name.includes('-A-') && location.name.includes('HVAC')
    );

    const hasEVLocation = selectedLocations.some(location => 
      location.name.includes('-A-') && location.name.includes('CHARGERS')
    );

    setSelectedAncillary(prev => {
      const poolOptions = ['pool-electricity-consumption', 'pool-electricity-total-consumption'];
      const hvacOptions = ['hvac-electricity-consumption', 'hvac-electricity-total-consumption'];
      const evOptions = ['ev-charger-electricity-consumption', 'ev-charger-total-consumption'];
      
      let newSelection = [...prev];
      
      if (hasPoolLocation) {
        // Add pool options if not already present
        newSelection = [...new Set([...newSelection, ...poolOptions])];
      } else {
        // Remove pool options if present
        newSelection = newSelection.filter(option => !poolOptions.includes(option));
      }
      
      if (hasHVACLocation) {
        // Add HVAC options if not already present
        newSelection = [...new Set([...newSelection, ...hvacOptions])];
      } else {
        // Remove HVAC options if present
        newSelection = newSelection.filter(option => !hvacOptions.includes(option));
      }

      if (hasEVLocation) {
        // Add EV options if not already present
        newSelection = [...new Set([...newSelection, ...evOptions])];
      } else {
        // Remove EV options if present
        newSelection = newSelection.filter(option => !evOptions.includes(option));
      }
      
      return newSelection;
    });
  }, [selectedLocations]);

  useEffect(()=>{
    const updatedEnvironmental = [];
    if(environmental.includes("outdoortemperature")){updatedEnvironmental.push("outdoortemperature")}
    if(environmental.includes("outdoorhumidity")){updatedEnvironmental.push("outdoorhumidity")}
    if(!selectedLocations.some(
      (location) => location.name.toLowerCase() === "0002-p-smiths lake nsw - nadine the cake queen"
    )){
      if(updatedEnvironmental.length>0){
        setEnvironmental(updatedEnvironmental);
      }
      else
      {
        setEnvironmental([]);
      }
    }
  }, [selectedLocations])


  const handleCountryChange = (selectedCountriesArray) => {
    setSelectedCountries(selectedCountriesArray);
    // Reset states and locations when countries change
    setSelectedStates([]);
    setSelectedLocations([]);
  };

  const isPowerActive = selectedLocations.some(location => (location.name.includes('-P-') || (!location.name.includes('-A-') && !location.name.includes('-P-'))));
  const isAncillaryActive = selectedLocations.some(location => location.name.includes('-A-'));
  const isHVAC = selectedLocations.some(location => location.name.includes('HVAC'));
  const isPool = selectedLocations.some(location => location.name.includes('POOL'));
  const isEV = selectedLocations.some(location => location.name.includes('CHARGERS'));
  const isRefrigeration = selectedLocations.some(location => location.name.includes('Refrigeration'));
  const isEnergy = selectedLocations.some(location => location.name.includes('Energy'));

  useEffect(() => {
    if (locations && locations.length > 0) {
      const isBelgiumSelected = selectedCountries.includes("Belgium");
      const isAustraliaSelected = selectedCountries.includes("Australia");
      
      const isBCRSelected = selectedStates.includes("BCR - Brussels-Capital Region");
      const isNSWSelected = selectedStates.includes("NSW - New South Wales");
  
      let filtered = [];
  
      if (isBelgiumSelected && selectedStates.length === 1 && isBCRSelected) {
        filtered = locations.filter(
          (location) => location.state === "BCR - Brussels-Capital Region"
        );
      } else if (isAustraliaSelected && selectedStates.length === 1 && isNSWSelected) {
        filtered = locations.filter(
          (location) => location.state === "NSW - New South Wales"
        );
      } else if (isBelgiumSelected && isAustraliaSelected && selectedStates.includes("BCR - Brussels-Capital Region") && selectedStates.includes("NSW - New South Wales")) {
        filtered = locations.filter(
          (location) => location.state === "BCR - Brussels-Capital Region" || location.state === "NSW - New South Wales"
        );
      }
      else if(selectedCountries.length === 1 && selectedStates.length > 0 && !(isBCRSelected || isNSWSelected)){
        filtered = [];
      } else {
        filtered = locations.filter(
          (location) => selectedStates.includes(location.state)
        );
      }

      // Further filter by location type
      filtered = filtered.filter(
        (location) =>
          selectedLocationType === "residential"
            ? !location.name.includes("-P-") && !location.name.includes("-A-")
            : location.name.includes("-P-") || location.name.includes("-A-")
      );

      if (selectedLocationType === "commercial") {
        filtered.sort((a, b) => {
          const numA = parseInt(a.name.split('-')[0], 10);
          const numB = parseInt(b.name.split('-')[0], 10);
          if (numA !== numB) {
            return numA - numB;
          }
          return a.name.localeCompare(b.name);
        });
      }
      
      setFilteredLocations(filtered);
    }
    setSelectedLocations([]);
  }, [locations, selectedCountries, selectedStates, selectedLocationType]);

  const handleLocationTypeChange = (value) => {
    setSelectedLocationType(value);
  };

  return (
    <Box p={{ base: 1, md: 2 }}>
      <Flex
        justifyContent="space-between"
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 5 }}
        width="100%"
      >
        <Flex
          direction="column"
          gap={3}
          width={{ base: "100%", md: "29%" }}
          minWidth={{ base: "100%", md: "260px" }}
          maxWidth={{ base: "100%", md: "350px" }}
          padding={{ base: 2, md: 5 }}
          height={{ base: "35vh", md: "82vh" }}
          overflowY={{ base: "auto", md: "auto" }}
          backgroundColor="white"
          boxShadow="0 4px 8px rgba(85,37,9,.05)"
        >
          <Flex direction="column" gap={5}>
            <Menu closeOnSelect={false}>
              <MenuButton
                fontWeight={500}
                as={Button}
                width="100%"
                textAlign="left"
                rightIcon={<RiArrowDropDownLine />}
                fontSize={{ base: "sm", md: "md" }}
                padding={{ base: 2, md: 3 }}
              >
                Select Countries
              </MenuButton>
              <MenuList
                display="flex"
                width="100%"
                minWidth={{ base: "90vw", md: "250px" }}
                maxHeight="300px"
                overflowY="auto"
                padding="9px"
                flexDirection="column"
              >
                <CheckboxGroup
                  colorScheme="black"
                  onChange={handleCountryChange}
                  value={selectedCountries}
                >
                  <Stack direction="column" spacing={2}>
                    {sortedCountries.map(({name}) => (
                      <Checkbox key={name} value={name} sx={checkboxStyle} fontSize={{ base: "sm", md: "md" }} fontWeight={name==="Australia" || name ==="Belgium"?"700":""}>
                        {name}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </MenuList>
            </Menu>

            {selectedCountries.length > 0 ?
              <Menu>
                <MenuButton
                  fontWeight={500}
                  as={Button}
                  width="100%"
                  textAlign="left"
                  rightIcon={<RiArrowDropDownLine />}
                  fontSize={{ base: "sm", md: "md" }}
                  padding={{ base: 2, md: 3 }}
                >
                  Select States
                </MenuButton>
                <MenuList
                  display="flex"
                  width="100%"
                  minWidth={{ base: "90vw", md: "250px" }}
                  maxHeight="300px"
                  overflowY="auto"
                  padding="9px"
                  flexDirection="column"
                >
                  <CheckboxGroup
                    colorScheme="black"
                    onChange={handleStatesChange}
                    value={selectedStates}
                  >
                    <Stack direction="column" spacing={2}>
                      {selectedCountries.includes('Australia') && availableStates['Australia'] && (
                        <>
                          <Text fontWeight="bold" px={2} mt={1}>Australia</Text>
                          {availableStates['Australia'].map((state) => (
                            <Checkbox key={`AU-${state}`} value={state} sx={checkboxStyle} fontSize={{ base: "sm", md: "md" }}>
                              {state}
                            </Checkbox>
                          ))}
                        </>
                      )}
                      {selectedCountries.includes('Belgium') && availableStates['Belgium'] && (
                        <>
                          <Text fontWeight="bold" px={2} mt={2}>Belgium</Text>
                          {availableStates['Belgium'].map((state) => (
                            <Checkbox key={`BE-${state}`} value={state} sx={checkboxStyle} fontSize={{ base: "sm", md: "md" }}>
                              {state}
                            </Checkbox>
                          ))}
                        </>
                      )}
                    </Stack>
                  </CheckboxGroup>
                </MenuList>
              </Menu>            :
            <Text fontSize={{ base: "sm", md: "md" }}>No countries selected</Text>}
            {selectedStates.length > 0 ?
            <Menu>
              <MenuButton
                fontWeight={500}
                as={Button}
                width="100%"
                textAlign="left"
                rightIcon={<RiArrowDropDownLine />}
                fontSize={{ base: "sm", md: "md" }}
                padding={{ base: 2, md: 3 }}
              >
                Select Location Type
              </MenuButton>
              <MenuList
                display="flex"
                width="100%"
                minWidth={{ base: "90vw", md: "250px" }}
                maxHeight="300px"
                overflowY="auto"
                padding="9px"
                flexDirection="column"
              >
                <RadioGroup 
                  value={selectedLocationType} 
                  onChange={handleLocationTypeChange}
                >
                  <Stack direction="column">
                    {locationTypes.map((type) => (
                      <Radio key={type.value} value={type.value} sx={checkboxStyle} fontSize={{ base: "sm", md: "md" }}>
                        {type.label}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              </MenuList>
            </Menu>
            :
            selectedStates.length > 0 ? <p>No locations found for this state</p> : <Skeleton height="40px" width="100%" />}
            {selectedStates.length > 0 && filteredLocations.length ?
              <Menu>
                <MenuButton
                  fontWeight={500}
                  as={Button}
                  width="100%"
                  textAlign="left"
                  rightIcon={<RiArrowDropDownLine />}
                  fontSize={{ base: "sm", md: "md" }}
                  padding={{ base: 2, md: 3 }}
                >
                  Select Locations
                </MenuButton>
                <MenuList
                  display="flex"
                  width="100%"
                  minWidth={{ base: "90vw", md: "250px" }}
                  maxHeight="300px"
                  overflowY="auto"
                  padding="9px"
                  flexDirection="column"
                >
                  <CheckboxGroup
                    colorScheme="black"
                    onChange={handleLocationChange}
                    value={selectedLocations.map((location) => JSON.stringify(location))}
                  >
                    <Stack direction="column" spacing={2}>
                      {filteredLocations.map((location, index) => (
                        <Checkbox
                          key={index}
                          value={JSON.stringify(location)}
                          sx={checkboxStyle}
                          fontSize={{ base: "sm", md: "md" }}
                        >
                          {location.name}
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </MenuList>
              </Menu>:
              selectedStates.length > 0 && filteredLocations.filter((location) => selectedStates.includes(location.state)).length===0?<p>No locations found</p>:<Skeleton height="40px" width="100%" />}
              {locationLoading || !selectedLocations.length ? (
                <Stack>
                  <Skeleton height={{ base: "40px", md: "40px" }} width="100%" marginBottom={3} />
                  
                  <div style={{border:'1px solid #CAD2DC', padding:'10px', marginBottom:"15px"}}>
                  
                  <div style={{display:"flex", width:"100%", justifyContent:"space-between"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  </div>
                  
                  <div style={{display:"flex", width:"100%", justifyContent:"space-between"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  </div>

                  <div style={{display:"flex", width:"100%", justifyContent:"space-between"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  </div>

                  <div style={{display:"flex", width:"100%", justifyContent:"space-between"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  </div>

                  <div style={{display:"flex", width:"100%", justifyContent:"space-between"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  </div>

                  </div>
                  
                  <div style={{border:'1px solid #CAD2DC', padding:'10px', marginBottom:"15px"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width="230px" />
                  </div>

                  <div style={{border:'1px solid #CAD2DC', padding:'10px'}}>
                  
                  <div style={{display:"flex", width:"100%", justifyContent:"space-between"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  </div>
                  
                  <div style={{display:"flex", width:"100%", justifyContent:"space-between"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  </div>

                  <div style={{display:"flex", width:"100%", justifyContent:"space-between"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  </div>

                  <div style={{display:"flex", width:"100%", justifyContent:"space-between"}}>
                  <Skeleton height={{ base: "25px", md: "25px" }} width={{ base: "230px", md: "230px" }} marginBottom={3} />
                  </div>

                  </div>

                </Stack>
              ) : (
                <>
                  <Select
                    placeholder="Select Time Period"
                    value={timePeriod}
                    variant="filled"
                    onChange={(e) => setTimePeriod(e.target.value)}
                    icon={<RiArrowDropDownLine />}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    <option value={"day"}>Day</option>
                    <option value={"week"}>Week</option>
                    <option value={"month"}>Month</option>
                    <option value={"quarter"}>Quarter</option>
                    <option value={"half-year"}>Half Year</option>
                    <option value={"year"}>Year</option>
                    <option value={"2years"}>2 Years</option>
                  </Select>

                  <fieldset
                    style={{
                      border: "2px solid black",
                      padding: "10px",
                      borderRadius: "5px",
                      opacity: isPowerActive ? 1 : 0.3,
                    }}
                    disabled={!isPowerActive}
                  >
                    <legend
                      style={{
                        color: "black",
                        fontSize: "1rem",
                        padding: "0 5px",
                      }}
                    >
                      Power :
                    </legend>
                    <CheckboxGroup
                      colorScheme="black"
                      value={selectedUtilities}
                      onChange={setSelectedUtilities}
                    >
                      <Stack direction="column">
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="net-zero-status"
                          >
                            BNZE Status
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="power-factor"
                          >
                            Power Factor
                          </Checkbox>
                          
                        </Stack>
                        {selectedLocations.some(
                          (location) => location.name !== "SMITHS LAKE  BUTCHERS"
                        ) &&
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="total-electricity-consumption"
                          >
                            Total Electricity Consumption
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="total-electricity-imported"
                          >
                            Total Electricity Imported from Grid
                          </Checkbox>
                          
                        </Stack>}
                        {selectedLocations.some(
                          (location) => location.name !== "NRMA PORT MACQUARIE SWITCHBOARD EAST"
                        ) && <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="solar-electricity-consumed"
                          >
                            Solar Electricity Consumed
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="solar-electricity-generated"
                          >
                            Solar Electricity Generated
                          </Checkbox>
                          {/* <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="solar-exported-to-grid"
                          >
                            Solar Electricity Exported to Grid
                          </Checkbox> */}
                        </Stack>}
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="ghg-emissions"
                          >
                            GHG Emissions (Net)
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="ghg-avoided"
                          >
                            GHG Emissions Avoided
                          </Checkbox>
                        </Stack>
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="demand-kvah"
                          >
                            Demand Active (kVAh)
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="demand-kvarh"
                          >
                            Demand Reactive (kVARh)
                          </Checkbox>
                        </Stack>
                        {/* <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="power-factor"
                          >
                            Power Factor
                          </Checkbox>
                        </Stack> */}
                      </Stack>
                    </CheckboxGroup>
                  </fieldset>

                  <fieldset
                    style={{
                      border: "2px solid black",
                      padding: "10px",
                      borderRadius: "5px",
                      opacity: isAncillaryActive ? 1 : 0.3,
                    }}
                    disabled={!isAncillaryActive}
                  >
                    <legend
                      style={{
                        color: "black",
                        fontSize: "1rem",
                        padding: "0 5px",
                      }}
                    >
                      Ancillary :
                    </legend>
                    <CheckboxGroup
                      colorScheme="black"
                      value={selectedAncillary}
                      onChange={setSelectedAncillary}
                    >
                      <Stack direction="column">
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="ev-charger-electricity-consumption"
                            disabled={!isEV}
                          >
                            EV Charger Electricity Consumption
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="ev-charger-total-consumption"
                            disabled={!isEV}
                          >
                            EV Charger % of Total Consumption
                          </Checkbox>
                        </Stack>
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="pool-electricity-consumption"
                            disabled={!isPool}
                          >
                            Pool Electricity Consumption
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="pool-electricity-total-consumption"
                            disabled={!isPool}
                          >
                            Pool Electricity % of Total Consumption
                          </Checkbox>
                        </Stack>
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="hvac-electricity-consumption"
                            disabled={!isHVAC}
                          >
                            HVAC Electricity Consumption
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="hvac-electricity-total-consumption"
                            disabled={!isHVAC}
                          >
                            HVAC Electricity % of Total Consumption
                          </Checkbox>
                        </Stack>
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="refrigeration-electricity-consumption"
                            disabled={!isRefrigeration}
                          >
                            Refrigeration Electricity Consumption
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="refrigeration-electricity-total-consumption"
                            disabled={!isRefrigeration}
                          >
                            Refrigeration Electricity % of Total Consumption
                          </Checkbox>
                        </Stack>
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="energy-savings-modelling"
                            disabled={!isEnergy}
                          >
                            Energy Savings modelling
                          </Checkbox>
                        </Stack>
                      </Stack>
                    </CheckboxGroup>
                  </fieldset>

                  <fieldset
                    style={{
                      border: "2px solid black",
                      padding: "10px",
                      borderRadius: "5px",
                      opacity: selectedLocations.some((location) => location.name.includes('Nadine The Cake Queen')) ? 1 : 0.3,
                    }}
                    disabled={!selectedLocations.some((location) => location.name.includes('Nadine The Cake Queen'))}
                  >
                    <legend
                      style={{
                        color: "black",
                        fontSize: "1rem",
                        padding: "0 5px",
                      }}
                    >
                      Utilities :
                    </legend>
                    <CheckboxGroup
                      colorScheme="black"
                      value={selectedOthers}
                      onChange={setSelectedOthers}
                    >
                      <Checkbox
                        sx={checkboxStyle}
                        width={"50%"}
                        value="total-water"
                      >
                        Water
                      </Checkbox>
                      {/* <Checkbox sx={checkboxStyle} width={'50%'} value="total-gas">Gas</Checkbox> */}
                    </CheckboxGroup>
                  </fieldset>

                    <fieldset
                    style={{
                      border: "2px solid black",
                      padding: "10px",
                      borderRadius: "5px",
                      opacity: selectedLocations.some((location) => location.name.includes('Nadine The Cake Queen')) ? 1 : 0.3,
                    }}
                    disabled={!selectedLocations.some((location) => location.name.includes('Nadine The Cake Queen'))}
                  >
                    <legend
                      style={{
                        color: "black",
                        fontSize: "1rem",
                        padding: "0 5px",
                      }}
                    >
                      Environmental :
                    </legend>
                    <CheckboxGroup
                      colorScheme="black"
                      value={environmental}
                      onChange={setEnvironmental}
                    >
                      <Stack direction="column">
                        {/* <p>Indoor Air Quality :</p> */}
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="outdoortemperature"
                          >
                            Outdoor Temperature
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="outdoorhumidity"
                          >
                            Outdoor Humidity
                          </Checkbox>
                        </Stack>
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="temperature"
                          >
                            Indoor Temperature
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="humidity"
                          >
                            Indoor Humidity
                          </Checkbox>
                        </Stack>
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="pm2.5"
                          >
                            PM2.5
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="tvoc"
                          >
                            TVOC
                          </Checkbox>
                        </Stack>
                        <Stack direction="row" spacing={4}>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="co2"
                          >
                            CO2
                          </Checkbox>
                          <Checkbox
                            sx={checkboxStyle}
                            width={"50%"}
                            value="dewPoint"
                          >
                            Dewpoint
                          </Checkbox>
                        </Stack>
                      </Stack>
                    </CheckboxGroup>
                  </fieldset>
                </>
              )}
        </Flex>
        </Flex>

        <Box
          width={{ base: "100%", md: "70%" }}
          minWidth={0}
          height={{ base: "auto", md: "82vh" }}
          overflowY={{ base: "visible", md: "auto" }}
          padding={{ base: "15px 0", md: "11px 15px" }}
          background="white"
        >
          {/* <Heading color={'#3c6a3a'} mb={2} size={"md"}>{`${user.company_name} Totals`}</Heading> */}
          {selectedLocations && (
            <Flex
              direction="row"
              flexWrap="wrap"
              columnGap="10px"
              rowGap="5px"
              alignItems="baseline"
              mb={2}
            >
              {selectedLocations.length > 0 && (
                <Heading size="sm" color="black">
                  Locations:{" "}
                </Heading>
              )}
              {selectedLocations?.map((location, index) => (
                <Text key={index} size="md">
                  {`${location.name},`}
                </Text>
              ))}
            </Flex>
          )}
          <Flex direction="row" gap="10px" alignItems="baseline">
            <Heading mb={4} size="sm" color="black">
              Time Period:{" "}
            </Heading>
            <Text size="md">
              {energyStartDate.toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
              }) ===
              energyEndDate.toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
              })
                ? energyEndDate.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  })
                : `${energyStartDate.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  })} - ${energyEndDate.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  })}`}
            </Text>
          </Flex>
          
          {selectedLocations.some(
                          (location) => (location.name.includes('-P-') || (!location.name.includes('-A-') && !location.name.includes('-P-')))
                        ) && 
          <Flex
            direction="row"
            flexWrap="wrap"
            justifyContent="start"
            gap="15px"
            mt={4}
          >
            {selectedUtilities.map((item, index) => (
             powerDataLoading ? 
              <Skeleton
                height={{ base: "180px", md: "110px" }}
                width={{ base: "171px", md: "130px" }}
                borderRadius={10}
              />:
              <TotalEmissionsBox
                unit={powerMetaData[item].unit}
                type={powerMetaData[item].name}
                emissions={powerData && powerData[powerMetaData[item].value]}
              />
            ))}
          </Flex>}

          {selectedLocations.some(
                          (location) => location.name.includes('-A-')) && 
          <Flex direction="row" flexWrap="wrap" gap="15px" mt={4}>
            {selectedAncillary.map((item, index) => (
              <TotalEmissionsBox
                unit={ancillaryMetaData[item].unit}
                type={ancillaryMetaData[item].name}
                emissions={ancillaryData && ancillaryData[ancillaryMetaData[item].value]}
              />
            ))}
          </Flex>}

          {selectedLocations.some(
                          (location) => location.name.includes('Nadine The Cake Queen') && location.name.includes('-P-')
                        ) && 
          <Flex direction="row" flexWrap="wrap" gap="15px" mt={4}>
            {selectedOthers.map((item, index) => (
              <TotalEmissionsBox
                unit="KL"
                type="Water"
                emissions={waterData}
              />
            ))}
          </Flex>}

          {selectedLocations.some(
                          (location) => location.name.includes('Nadine The Cake Queen') && location.name.includes('-P-')) && 
          <Flex direction="row" flexWrap="wrap" gap="15px" mt={4}>
            {environmental.map((item, index) => (
              <TotalEmissionsBox
                unit={envMetaData[item].unit}
                type={envMetaData[item].name}
                emissions={envData && envData[envMetaData[item].value]}
              />
            ))}
          </Flex>}

          <Button
            className="download_btn"
            mt={4}
            width={{ base: "100%", md: "auto" }}
            backgroundColor="black"
            color="white"
            onClick={handleDownloadReport}
            fontSize={{ base: "sm", md: "md" }}
            isDisabled={isDownloading}
            leftIcon={isDownloading ? <Spinner size="sm" color="white" /> : undefined}
          >
            {isDownloading ? "Downloading..." : "Download Report"}
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

export default TotalTabs;
